import { isPlatform } from '@ionic/vue'
import { createError, navigateTo, useNuxtApp } from '#imports'
import type { AuthConfig } from '../types/AuthConfig'
import type { Auth } from '../types/Auth'
import type { TokenResponse } from '../types/TokenResponse'
import { AppAuthErrorCode } from '../types/AppAuthErrorCode'
import { AppAuthCapacitor } from '../capacitor'

class AppAuthPluginError extends Error {
	code?: string
	errorMessage?: string
}

function isAppAuthPluginError(error: unknown): error is AppAuthPluginError {
	return 'code' in (error as Error)
}

export default class AppAuth implements Auth {
	private nuxtApp = useNuxtApp()

	async init(config: AuthConfig) {
		console.log('Initializing AppAuth with config:', config)

		try {
			await AppAuthCapacitor.init({
				config: {
					url: config.url,
					realm: config.realm,
					clientId: config.clientId,
					redirectUri: config.redirectUri,
					allowHttp: config.allowHttp ?? false, // Default to false if not provided
				},
			})
		} catch (error) {
			throw createError({
				statusCode: 500,
				statusMessage: 'AppAuth plugin initialization failed',
				cause: error,
			})
		}
	}

	async signIn() {
		try {
			return await AppAuthCapacitor.signIn() as TokenResponse
		} catch (error) {
			if (isAppAuthPluginError(error)) {
				if (error.code === AppAuthErrorCode.USER_CANCELLED) {
					return Promise.resolve(undefined)
				}
			}
			throw createError({
				statusCode: 500,
				statusMessage: 'AppAuth sign-in failed',
				cause: error,
			})
		}
	}

	async signOut(idToken?: string) {
		if (idToken) {
			await AppAuthCapacitor.signOut({ idToken })
			// Auf Mobilgeräten (Capacitor) erfolgt nach dem Logout keine Weiterleitung durch AppAuth,
			// daher müssen wir manuell zur Login-Seite navigieren.
			// Im Web leitet KeycloakJs.logout() über den end_session-Endpunkt von Keycloak weiter.
			if (isPlatform('capacitor')) {
				await navigateTo('/')
			}
		} else {
			throw createError({
				statusCode: 400,
				statusMessage: 'signOut() called without an idToken',
			})
		}
	}

	async signUp() {
		try {
			await AppAuthCapacitor.signUp()
		} catch (error) {
			if (isAppAuthPluginError(error)) {
				if (error.code === AppAuthErrorCode.USER_CANCELLED || error.code === AppAuthErrorCode.EMAIL_VERIFICATION_PENDING) {
					return Promise.resolve()
				}
			}
			throw createError({
				statusCode: 500,
				statusMessage: 'AppAuth sign-up failed',
				cause: error,
			})
		}
	}

	async refreshToken(refreshToken: string) {
		try {
			return await AppAuthCapacitor.refreshToken({ refreshToken }) as TokenResponse
		} catch (error) {
			throw createError({
				statusCode: 500,
				statusMessage: 'AppAuth token refresh failed',
				cause: error,
			})
		}
	}
}
